import React, { useState, useEffect, useRef } from "react";
import config from "../config";
import "./AccessControl.css";

const ROLES = [
  "AM",
  "BP",
  "BMG",
  "CM",
  "DEV-B",
  "DM",
  "MED",
  "PM",
  "PR",
  "QA",
  "SEO",
  "Top Manager",
  "TRAFF",
  "UX",
];

const AccessControl = ({ value = [], onChange }) => {
  const [selectedAccess, setSelectedAccess] = useState(value);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [tpUsers, setTpUsers] = useState([]);
  const [userNameMap, setUserNameMap] = useState({}); // Stores mapping of username to full name
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // 'all', 'role', 'user'

  // Use ref to prevent infinite loops
  const isInitialMount = useRef(true);
  const previousValue = useRef(value);

  // Fetch TP users when component mounts
  useEffect(() => {
    const fetchTpUsers = async () => {
      setIsLoadingUsers(true);
      setErrorMessage("");
      try {
        const response = await fetch(
          `${config.api.baseUrl}/dashboard/active-tp-users`,
          { credentials: "include" }
        );
        const data = await response.json();
        if (data.success) {
          setTpUsers(data.data || []);

          // Create a mapping of usernames to full names
          const nameMap = {};
          data.data.forEach((user) => {
            const username = user.email.split("@")[0];
            nameMap[username] = `${user.firstName} ${user.lastName}`;
          });
          setUserNameMap(nameMap);
        } else {
          throw new Error(data.error || "Failed to fetch users");
        }
      } catch (err) {
        setErrorMessage("Error loading users: " + err.message);
        console.error("Error fetching TP users:", err);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchTpUsers();
  }, []);

  // Update selectedAccess when value prop changes (from parent)
  useEffect(() => {
    // Only update if the arrays are actually different
    if (JSON.stringify(value) !== JSON.stringify(previousValue.current)) {
      setSelectedAccess(value);
      previousValue.current = value;
    }
  }, [value]);

  // Effect to propagate changes to parent, but only after initial mount
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only call onChange if our internal state differs from parent's value
    if (JSON.stringify(selectedAccess) !== JSON.stringify(value)) {
      onChange(selectedAccess);
    }
  }, [selectedAccess, onChange, value]);

  // Handle selecting the "All" option - toggle behavior
  const handleSelectAll = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (selectedAccess.includes("All")) {
      // If "All" is already selected, remove it
      setSelectedAccess([]);
    } else {
      // Otherwise add it, removing any other selected options
      setSelectedAccess(["All"]);
    }
  };

  // Handle selecting a role - toggle behavior
  const handleSelectRole = (e, role) => {
    e.preventDefault();
    e.stopPropagation();

    if (selectedAccess.includes("All")) {
      // If "All" is already selected, replace it with this role
      setSelectedAccess([role]);
    } else if (selectedAccess.includes(role)) {
      // If role is already selected, remove it
      setSelectedAccess(selectedAccess.filter((item) => item !== role));
    } else {
      // Otherwise add it to the selection
      setSelectedAccess([...selectedAccess, role]);
    }
  };

  // Handle user search
  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (query.length > 1) {
      setIsSearching(true);
      // Search local users in parallel to TP users
      Promise.all([
        Promise.resolve(
          tpUsers.filter(
            (user) =>
              user.email?.toLowerCase().includes(query) ||
              user.firstName?.toLowerCase().includes(query) ||
              user.lastName?.toLowerCase().includes(query)
          )
        ),
        fetch(
          `${config.api.baseUrl}/auth/users/local-search?q=${encodeURIComponent(
            query
          )}`,
          {
            credentials: "include",
          }
        )
          .then((r) => r.json())
          .then((d) =>
            d && d.success && Array.isArray(d.users) ? d.users : []
          )
          .catch(() => []),
      ]).then(([tp, local]) => {
        const merged = [...tp, ...local]
          .filter(
            (u, idx, arr) =>
              idx ===
              arr.findIndex(
                (x) =>
                  x.email &&
                  x.email.toLowerCase() === (u.email || "").toLowerCase()
              )
          )
          .slice(0, 8);
        setSearchResults(merged);
      });
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  };

  // Handle selecting a user from search results
  const handleSelectUser = (e, user) => {
    e.preventDefault();
    e.stopPropagation();

    // Extract username from email (before @)
    const username = user.email.split("@")[0];

    // Update the user name map if not already there
    if (!userNameMap[username]) {
      setUserNameMap((prev) => ({
        ...prev,
        [username]: `${user.firstName} ${user.lastName}`,
      }));
    }

    // If "All" is already selected, clear it
    if (selectedAccess.includes("All")) {
      setSelectedAccess([username]);
    } else if (selectedAccess.includes(username)) {
      // If username is already selected, remove it (toggle behavior)
      setSelectedAccess(selectedAccess.filter((item) => item !== username));
    } else {
      // Otherwise add the username
      setSelectedAccess([...selectedAccess, username]);
    }

    setSearchQuery("");
    setSearchResults([]);
    setIsSearching(false);
  };

  // Handle removing a selected option
  const handleRemoveOption = (e, option) => {
    e.preventDefault();
    e.stopPropagation();

    setSelectedAccess(selectedAccess.filter((item) => item !== option));
  };

  // Get display name for a user ID
  const getDisplayName = (userId) => {
    if (userId === "All") return "Everyone";
    if (ROLES.includes(userId)) return userId;
    return userNameMap[userId] || userId;
  };

  // Render the access tags
  const renderAccessTags = () => {
    return selectedAccess.map((item) => (
      <div key={item} className="access-tag">
        <span className="access-tag-text">{getDisplayName(item)}</span>
        <button
          type="button"
          className="access-tag-remove"
          onClick={(e) => handleRemoveOption(e, item)}
        >
          ×
        </button>
      </div>
    ));
  };

  return (
    <div className="access-control">
      <div className="access-tabs">
        <button
          type="button"
          className={`access-tab ${activeTab === "all" ? "active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveTab("all");
          }}
        >
          Everyone
        </button>
        <button
          type="button"
          className={`access-tab ${activeTab === "role" ? "active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveTab("role");
          }}
        >
          By Role
        </button>
        <button
          type="button"
          className={`access-tab ${activeTab === "user" ? "active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveTab("user");
          }}
        >
          By User
        </button>
      </div>

      <div className="access-content">
        {activeTab === "all" && (
          <div className="access-all-section">
            <p>Grant access to everyone in the organization</p>
            <button
              type="button"
              className={`access-all-button ${
                selectedAccess.includes("All") ? "active" : ""
              }`}
              onClick={handleSelectAll}
            >
              {selectedAccess.includes("All")
                ? "Access granted to everyone"
                : "Grant access to everyone"}
            </button>
          </div>
        )}

        {activeTab === "role" && (
          <div className="access-role-section">
            <p>Select specific roles to grant access</p>
            <div className="role-grid">
              {ROLES.map((role) => (
                <div
                  key={role}
                  className={`role-option ${
                    selectedAccess.includes(role) ? "selected" : ""
                  }`}
                  onClick={(e) => handleSelectRole(e, role)}
                >
                  {role}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "user" && (
          <div className="access-user-section">
            <p>Search for specific users by name or email</p>
            <div className="user-search-container">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Start typing a name or email..."
                className="user-search-input"
              />

              {isLoadingUsers && (
                <div className="loading-message">Loading users...</div>
              )}
              {errorMessage && (
                <div className="error-message">{errorMessage}</div>
              )}

              {isSearching && searchResults.length > 0 && (
                <ul className="user-search-results">
                  {searchResults.map((user) => (
                    <li
                      key={user.email}
                      onClick={(e) => handleSelectUser(e, user)}
                      className="user-search-result"
                    >
                      {user.avatarUri ? (
                        <img
                          src={`${config.api.baseUrl}${
                            config.api.endpoints.dashboard.tpAvatar
                          }?url=${encodeURIComponent(user.avatarUri)}`}
                          alt={`${user.firstName} ${user.lastName}`}
                          className="user-avatar"
                        />
                      ) : (
                        <span
                          className="user-avatar"
                          dangerouslySetInnerHTML={{
                            __html: `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 40 40'>
  <circle cx='20' cy='20' r='20' fill='#3469ff'/>
  <text x='50%' y='50%' fill='#ffffff' font-family='Roboto, sans-serif' font-size='14' font-weight='600' text-anchor='middle' dominant-baseline='central'>${`${(
    user.firstName || "?"
  )
    .charAt(0)
    .toUpperCase()}${(user.lastName || "?").charAt(0).toUpperCase()}`}</text>
</svg>`,
                          }}
                        />
                      )}
                      <div className="user-info">
                        <div className="user-name">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="user-email">{user.email}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {isSearching &&
                searchResults.length === 0 &&
                searchQuery.length > 1 && (
                  <div className="no-results">No matching users found</div>
                )}
            </div>
          </div>
        )}
      </div>

      {selectedAccess.length > 0 && (
        <div className="selected-access">
          <div className="selected-access-label">Selected access:</div>
          <div className="access-tags">{renderAccessTags()}</div>
        </div>
      )}
    </div>
  );
};

export default AccessControl;
